import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import { Wind, Users, Shield, Award, MapPin } from 'lucide-react';

const Windsurfing = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  return (
    <div className="min-h-screen" data-testid="windsurfing-page">
      {/* Hero with Image */}
      <section className="relative h-[60vh] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_surfapp-manager/artifacts/gjr56kog_windsurfheader.jpg" 
            alt="Windsurfing"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-cyan-900/70"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <Wind className="w-20 h-20 mx-auto mb-6 text-cyan-400" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{t('windsurfing.title')}</h1>
            <p className="text-2xl text-blue-100 max-w-2xl mx-auto">{t('windsurfing.subtitle')}</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">{t('windsurfing.spot_title')}</h2>
            <p className="text-lg text-gray-600 leading-relaxed">{t('windsurfing.spot_desc')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            <div className="group bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3">{t('windsurfing.beginner')}</h3>
              <p className="text-gray-600 text-lg">{t('windsurfing.beginner_desc')}</p>
            </div>
            <div className="group bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3">{t('windsurfing.advanced')}</h3>
              <p className="text-gray-600 text-lg">{t('windsurfing.advanced_desc')}</p>
            </div>
            <div className="group bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3">{t('windsurfing.equipment')}</h3>
              <p className="text-gray-600 text-lg">{t('windsurfing.equipment_desc')}</p>
            </div>
          </div>

          {/* Detailed Information Sections */}
          <div className="space-y-16">
            {/* Location */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-10 md:p-12">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-4">{t('windsurfing.location_title')}</h2>
                  <p className="text-lg text-gray-700 leading-relaxed">{t('windsurfing.location_desc')}</p>
                </div>
              </div>
            </div>

            {/* Wind */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-10 md:p-12">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Wind className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-4">{t('windsurfing.wind_title')}</h2>
                  <p className="text-lg text-gray-700 leading-relaxed">{t('windsurfing.wind_desc')}</p>
                </div>
              </div>
            </div>

            {/* Our School */}
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-3xl p-10 md:p-12">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-4">{t('windsurfing.school_title')}</h2>
                  <p className="text-lg text-gray-700 leading-relaxed">{t('windsurfing.school_desc')}</p>
                </div>
              </div>
            </div>

            {/* Our Courses */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-10 md:p-12">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-4">{t('windsurfing.courses_title')}</h2>
                  <p className="text-lg text-gray-700 leading-relaxed">{t('windsurfing.courses_desc')}</p>
                </div>
              </div>
            </div>

            {/* Safety */}
            <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-3xl p-10 md:p-12">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-rose-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-4">{t('windsurfing.safety_title')}</h2>
                  <p className="text-lg text-gray-700 leading-relaxed">{t('windsurfing.safety_desc')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Image Gallery */}
          <div className="mt-16 grid grid-cols-2 gap-4">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_surfapp-manager/artifacts/yol0rzdt_raceweek.jpg" 
                alt="Windsurfing Race Week"
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_surfapp-manager/artifacts/bl045ohd_IMG_3041-scaled.jpg" 
                alt="Windsurfing Action"
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Windsurfing;